import * as _ from "lodash";
import { OfferInfo } from "~/app/core/model/offer-info";
import { toDate, toMoment } from "~/app/core/model/date-str";
import { LocationInfo } from "~/app/core/model/location-info";

export class OfferItemView {
    static isOfferRedeemed(offerInfo: OfferInfo, now: Date): boolean {
        if (!now) now = new Date();
        return (offerInfo && offerInfo.redeemedDate && (!offerInfo.nextAvailableDate || OfferItemView.calculateDaysTillAvailable(now, offerInfo) > 0)) ? true : false;
    }

    static isOfferRepeatUnlockable(offerInfo: OfferInfo, now: Date): boolean {
        if (!now) now = new Date();
        return offerInfo && offerInfo.redeemedDate && !offerInfo.repeatUnlocked && offerInfo.nextAvailableDate && OfferItemView.calculateDaysTillAvailable(now, offerInfo) <= 0;
    }

    static isOfferFavorited(offerInfo: OfferInfo): boolean {
        return (offerInfo && offerInfo.favorite) ? true : false;
    }

    static isOfferFree(offerInfo: OfferInfo): boolean {
        return (offerInfo && offerInfo.free) ? true : false;
    }

    static isOfferShippedFree(offerInfo: OfferInfo): boolean {
        return (offerInfo.onlineOnly && offerInfo.shipping === "free") ? true : false;
    }

    static isOfferShippedAdditional(offerInfo: OfferInfo): boolean {
        return (offerInfo.onlineOnly && offerInfo.shipping === "additional") ? true : false;
    }

    static isOfferReadyForBounceback(currentTime: Date, offerInfo: OfferInfo): boolean {
        if (offerInfo && offerInfo.redeemedDate && offerInfo.bounceBackOfferId && offerInfo.bouncebackDate && !offerInfo.thumbsDate) {
            const bounceBackMillis = toDate(offerInfo.bouncebackDate).getTime();
            const currentMillis = currentTime.getTime();
            return currentMillis > bounceBackMillis;
        }
        return false;
    }

    static isOfferPurchaseSurveyNeeded(offerInfo: OfferInfo): boolean {
        if (offerInfo && !offerInfo.onlineOnly && offerInfo.redeemedDate && !offerInfo.purchaseSurveyDate) {
            // only solicit the survey if not too much time has elapsed
            const daysSinceRedeem = toMoment(new Date()).add(1, "days").diff(toMoment(offerInfo.redeemedDate), "hours")
            if (daysSinceRedeem <= 1) {
                return true;
            }
        }
        return false;
    }

    static isOfferNew(currentTime: Date, offerInfo: OfferInfo, daysToBeNew: number = 7): boolean {
        if (!offerInfo) return false;
        // treat offer as new if it went live in the past "daysToBeNew" days
        if (offerInfo.startDate) {
            const newUntilDate = toMoment(offerInfo.startDate).add("days", daysToBeNew);
            if (newUntilDate.toDate().getTime() > currentTime.getTime()) return true;
        }
        if (offerInfo.redeemedDate) return false;
        if (offerInfo.nextAvailableDate) return false;
        if (!offerInfo.seenDate) return true;
        const diffMillis = currentTime.getTime() - toDate(offerInfo.seenDate).getTime();
        return diffMillis < (60 * 1000 * 10); // 10 minutes
    }

    static isOfferUnseenLately(currentTime: Date, offerInfo: OfferInfo, daysToBeUnseen: number = 21): boolean {
        if (!offerInfo) return false;
        if (offerInfo.unseenUntilDate) {
            const newUntilDate = toMoment(offerInfo.unseenUntilDate);
            if (newUntilDate.toDate().getTime() > currentTime.getTime()) return true;
        }

        if (!offerInfo.lastSeenDate) return false;
        if (offerInfo.redeemedDate) return false;
        if (offerInfo.nextAvailableDate) return false;
        const newAfter = toMoment(offerInfo.lastSeenDate).add(daysToBeUnseen, "days");

        return currentTime.getTime() > newAfter.toDate().getTime();
    }

    static calculateNearestDistanceInMiles(offerInfo: OfferInfo, location: LocationInfo): number {
        let minDistance = null;
        if (offerInfo && location) {
            offerInfo.addresses.forEach((addr) => {
                if (addr.longitude && addr.latitude) {
                    const distance = LocationInfo.milesAway(
                        { longitude: addr.longitude, latitude: addr.latitude },
                        location
                    );
                    if (distance !== null) {
                        if (!minDistance || distance < minDistance) {
                            minDistance = distance;
                        }
                    }
                }
            });
        }
        return minDistance === null ? 0 : minDistance;
    }

    static calculateDaysRemaining(currentTime: Date, offerInfo: OfferInfo): number {
        if (!offerInfo) return 0;
        if (!offerInfo.expirationDate) return 100;
        const a = toMoment(currentTime);
        const b = toMoment(offerInfo.expirationDate);

        if (b.isBefore(a)) {
            return 0;
        } else {
            // console.log("now: "+ toDateStr(a) + " expire: " + toDateStr(b) + " remain: " + b.diff(a, "days"));
            return b.diff(a, "days") + 1;
        }
    }

    static calculateDaysTillAvailable(currentTime: Date, offerInfo: OfferInfo): number {
        if (!offerInfo) return 0;
        if (!offerInfo.nextAvailableDate) return 0;
        const a = toMoment(currentTime);
        const b = toMoment(offerInfo.nextAvailableDate);

        if (b.isBefore(a)) {
            return 0;
        } else {
            // console.log("now: "+ toDateStr(a) + " expire: " + toDateStr(b) + " remain: " + b.diff(a, "days"));
            return b.diff(a, "days") + 1;
        }
    }

    static calculateSecondsSinceRedeem(currentTime: Date, offerInfo: OfferInfo): number {
        if (offerInfo && offerInfo.redeemedDate) {
            const redeemedDate: Date = toDate(offerInfo.redeemedDate);
            return (currentTime.getTime() - redeemedDate.getTime()) / 1000;
        }
        return null;
    }

    visible: boolean = true;
    lastFraction: number = 0;
    fraction: number = 0;

    showMystery: boolean = true;
    showButtons: boolean = true;
    showExpiration: boolean = false;
    showRedeemNow: boolean = false;
    unavailable: boolean = false;
    milesAway: number = null;
    city: string = null;

    constructor(visible: boolean = false) {
        this.visible = visible;
    }

    deserialize(input: any) {
        _.merge(this, input);
    }
}
